import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip } from '@mui/material';
import toast from 'react-hot-toast';
import apiClient from '../services/apiClient';
import ProfileIcon from '../components/ProfileIcon';
import FeedbackModal from '../components/FeedbackModal';
import './ChallengeList.css';

const difficultyColors = {
  easy: { bg: 'rgba(52, 211, 153, 0.1)', color: '#34d399', border: 'rgba(52, 211, 153, 0.3)' },
  medium: { bg: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24', border: 'rgba(251, 191, 36, 0.3)' },
  hard: { bg: 'rgba(248, 113, 113, 0.1)', color: '#f87171', border: 'rgba(248, 113, 113, 0.3)' },
};

const categoryIcons = {
  'distributed-systems': '🌐',
  'storage': '💾',
  'messaging': '📨',
  'caching': '⚡',
  'networking': '🔗',
  'default': '📊',
};

function ChallengeList() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const handleFeedback = () => {
    setShowFeedbackModal(true);
  };

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const result = await apiClient.getQuestions();
        setQuestions(result.questions || []);
      } catch (err) {
        toast.error('Failed to load challenges');
      } finally {
        setLoading(false);
      }
    };
    loadQuestions();
  }, []);

  const filteredQuestions = questions.filter((q) => {
    const matchesDifficulty = selectedDifficulty === 'all' || q.difficulty === selectedDifficulty;
    const matchesSearch = q.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDifficulty && matchesSearch;
  });

  const handleSelectChallenge = (question) => {
    navigate(`/design?question=${question.id}`);
  };

  const getCategoryIcon = (category) => {
    return categoryIcons[category] || categoryIcons.default;
  };

  const stats = {
    total: questions.length,
    easy: questions.filter(q => q.difficulty === 'easy').length,
    medium: questions.filter(q => q.difficulty === 'medium').length,
    hard: questions.filter(q => q.difficulty === 'hard').length,
  };

  return (
    <div className="challenge-page">
      {/* Header */}
      <header className="challenge-header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">◈</span>
            <span className="logo-text">SystemCraft_</span>
          </div>
        </div>
        <div className="header-right">
          <button className="btn btn-feedback" onClick={handleFeedback} title="Share Feedback">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            Feedback
          </button>
          <ProfileIcon />
        </div>
      </header>

      {/* Hero Section */}
      <div className="hero-section">
        <h1>System Design Challenges</h1>
        <p>Master system design through hands-on practice. Draw, submit, and get AI-powered feedback.</p>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-item easy">
          <span className="stat-value">{stats.easy}</span>
          <span className="stat-label">Easy</span>
        </div>
        <div className="stat-item medium">
          <span className="stat-value">{stats.medium}</span>
          <span className="stat-label">Medium</span>
        </div>
        <div className="stat-item hard">
          <span className="stat-value">{stats.hard}</span>
          <span className="stat-label">Hard</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search challenges..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="difficulty-filters">
          {['all', 'easy', 'medium', 'hard'].map((diff) => (
            <button
              key={diff}
              className={`filter-btn ${selectedDifficulty === diff ? 'active' : ''} ${diff}`}
              onClick={() => setSelectedDifficulty(diff)}
            >
              {diff.charAt(0).toUpperCase() + diff.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Challenge List */}
      <div className="challenges-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading challenges...</p>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon"></span>
            <p>No challenges found</p>
          </div>
        ) : (
          <div className="challenges-grid">
            {filteredQuestions.map((question, index) => (
              <div
                key={question.id}
                className="challenge-card"
                onClick={() => handleSelectChallenge(question)}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="card-header">

                  <Chip
                    label={question.difficulty?.toUpperCase()}
                    size="small"
                    sx={{
                      backgroundColor: difficultyColors[question.difficulty]?.bg,
                      color: difficultyColors[question.difficulty]?.color,
                      border: `1px solid ${difficultyColors[question.difficulty]?.border}`,
                      fontWeight: 600,
                      fontSize: '10px',
                    }}
                  />
                </div>
                <h3 className="card-title">{question.title}</h3>
                <p className="card-description">{question.description}</p>
                <div className="card-footer">
                  <div className="card-tags">
                    {question.tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="tag">{tag}</span>
                    ))}
                  </div>
                  <button className="start-btn">
                    Start →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)} 
      />
    </div>
  );
}

export default ChallengeList;
